export type ExportColumn = {
  key: string;
  label: string;
  value?: (row: any) => any;
};

const csvEscape = (value: any) => {
  if (value === null || value === undefined) {
    return '';
  }
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadFile = (content: string, filename: string, mime: string) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const exportToCsv = (rows: any[], columns: ExportColumn[], filename: string) => {
  const header = columns.map(col => csvEscape(col.label)).join(',');
  const lines = rows.map(row =>
    columns.map(col => csvEscape(col.value ? col.value(row) : row?.[col.key])).join(',')
  );
  const csv = [header, ...lines].join('\r\n');
  downloadFile(csv, filename, 'text/csv;charset=utf-8;');
};

export const exportToExcel = (rows: any[], columns: ExportColumn[], filename: string) => {
  // Export as TSV so Excel splits columns correctly when opened.
  // (This project historically saves with .xls extension.)
  const header = columns.map(col => csvEscape(col.label)).join('\t');
  const lines = rows.map(row =>
    columns.map(col => csvEscape(col.value ? col.value(row) : row?.[col.key])).join('\t')
  );
  const tsv = [header, ...lines].join('\r\n');
  downloadFile(tsv, filename, 'application/vnd.ms-excel');
};

export const exportToPdf = (rows: any[], columns: ExportColumn[], title: string) => {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    return;
  }
  const tableHead = columns.map(col => `<th>${col.label}</th>`).join('');
  const tableBody = rows
    .map(row => {
      const cells = columns
        .map(col => `<td>${col.value ? col.value(row) : row?.[col.key] ?? ''}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  win.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 18px; margin-bottom: 16px; }
          table { border-collapse: collapse; width: 100%; font-size: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; }
          th { background: #f3f4f6; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <table>
          <thead><tr>${tableHead}</tr></thead>
          <tbody>${tableBody}</tbody>
        </table>
      </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
};

export const parseCsvToObjects = (content: string) => {
  const text = content.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && next === '\n') {
        i += 1;
      }
      row.push(current);
      if (row.some(cell => cell.trim() !== '')) {
        rows.push(row);
      }
      row = [];
      current = '';
      continue;
    }
    if (!inQuotes && char === ',') {
      row.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  if (current.length || row.length) {
    row.push(current);
    if (row.some(cell => cell.trim() !== '')) {
      rows.push(row);
    }
  }

  if (!rows.length) {
    return [];
  }

  const headers = rows[0].map(header => header.trim()).filter(Boolean);
  return rows.slice(1).map(values => {
    const obj: Record<string, any> = {};
    headers.forEach((header, index) => {
      obj[header] = values[index] ?? '';
    });
    return obj;
  });
};

